import { Component, createRef } from "react"

/* Modal */
import Modal from "react-modal"

/* Components */
import { Icon } from "../../components"

/* Constants */
import { env } from "../../constants"

/* REST API */
import { createTemplate, uploadImage, removeImage } from "../../api/Notification"

/* Widgets */
import { toast } from "../common"
import Preview from "./Preview"

/* Shared */
import { TYPES } from "./shared"

Modal.setAppElement("#root")

const INITIAL_FORM = { type: "client", title: "", description: "", button: "", buttonColor: "#0279fc", screen: "", params: "", image: "" }

/* Smileys at hand — a push without one reads like a receipt */
const EMOJI = ["😊", "😍", "🥳", "🔥", "✨", "🎉", "🎁", "💐", "💇", "💅", "💈", "🚗", "⏰", "📅", "📍", "✅", "❤️", "👍", "💰", "🏷️", "⭐", "🙌"]


/* Widget Create Modal — the form of a new template, a panel which slides in from the right.
   The card of the phone stands beside the fields and changes with every letter, so the text
   is written against what the client will actually see */
class CreateModal extends Component {

    constructor(props) {
        super(props)

        this.state = {
            saving: false,
            uploading: false,
            emoji: "",
            form: { ...INITIAL_FORM }
        }

        this._title = createRef()
        this._description = createRef()
        this._file = createRef()
    }

    componentDidUpdate(previous) {
        if (!previous.visible && this.props.visible) {
            const { initial } = this.props
            this.setState({ form: initial ? { ...INITIAL_FORM, ...initial } : { ...INITIAL_FORM }, emoji: "" })
        }
    }

    setForm = (key, value) => {
        this.setState(previous => ({ form: { ...previous.form, [key]: value } }))
    }

    /* The smiley lands where the cursor stands, not at the end of the line */
    insertEmoji = (field, emoji) => {

        const input = field === "title" ? this._title.current : this._description.current
        const value = this.state.form[field] ? this.state.form[field] : ""

        let start = value.length
        let end = value.length

        if (input && typeof input.selectionStart === "number") {
            start = input.selectionStart
            end = input.selectionEnd
        }

        this.setForm(field, `${value.slice(0, start)}${emoji}${value.slice(end)}`)
        this.setState({ emoji: "" })

        if (input) {
            const position = start + emoji.length
            setTimeout(() => {
                input.focus()
                input.setSelectionRange(position, position)
            }, 0)
        }
    }

    /* Picture of the notification — it is saved at once, so the preview is the real file */
    pickImage = event => {

        const file = event.target.files && event.target.files[0]

        if (!file) {
            return
        }

        const body = new FormData()
        body.append("image", file)

        this.setState({ uploading: true })

        uploadImage(body).then(response => {
            this.setState({ uploading: false })
            if (response && response.status === 200) {
                this.setForm("image", response.data.image)
            }
            else {
                toast.error("Не удалось загрузить фото")
            }
        })

        event.target.value = ""
    }

    clearImage = () => {

        const { form } = this.state

        if (form.image) {
            removeImage(form.image)
        }

        this.setForm("image", "")
    }

    save = () => {

        /* Fields */
        const { form, saving } = this.state
        const { onClose, onSaved } = this.props

        if (saving) {
            return
        }

        if (!form.title.trim()) {
            toast.error("Введите заголовок")
            return
        }

        if (!form.description.trim()) {
            toast.error("Введите текст уведомления")
            return
        }

        this.setState({ saving: true })

        createTemplate({
            type: form.type,
            title: form.title,
            description: form.description,
            button: form.button ? form.button : null,
            buttonColor: form.buttonColor ? form.buttonColor : null,
            screen: form.screen ? form.screen : null,
            params: form.params ? form.params : null,
            image: form.image ? form.image : null
        }).then(response => {

            this.setState({ saving: false })

            if (response && response.status === 200) {
                this.setState({ form: { ...INITIAL_FORM } })
                onClose()
                onSaved && onSaved()
                toast.success("Шаблон создан")
            }
            else {
                toast.error("Ошибка при сохранении")
            }
        })
    }

    /* Row of smileys under the field it writes into */
    _emoji = field => {

        /* Fields */
        const { emoji } = this.state

        if (emoji !== field) {
            return (
                <div onClick={() => this.setState({ emoji: field })} className="nt-emoji-open">
                    <span>🙂</span>
                    <span>Смайлики</span>
                </div>
            )
        }

        return (
            <div className="nt-emoji-list">

                {EMOJI.map((item, index) =>
                    <div key={`${index}`} onClick={() => this.insertEmoji(field, item)} className="nt-emoji-item">{item}</div>
                )}

                <div onClick={() => this.setState({ emoji: "" })} className="nt-emoji-close">
                    <Icon name="close" size={10} width={2.4} />
                </div>

            </div>
        )
    }

    /* The picture of the template */
    _image = () => {

        /* Fields */
        const { form, uploading } = this.state

        if (form.image) {
            return (
                <div className="nt-image-preview">
                    <img src={`${env.mediapoint}notifications/${form.image}`} alt="Notification" />
                    <div onClick={this.clearImage} className="nt-image-remove">
                        <Icon name="close" size={10} width={2.4} />
                    </div>
                </div>
            )
        }

        return (
            <div onClick={() => !uploading && this._file.current.click()} className={`nt-image-upload ${uploading ? "disabled" : ""}`}>
                <Icon name="image" size={20} />
                <span>{uploading ? "Загрузка..." : "Загрузить фото"}</span>
                <p>JPG или PNG, видно в приложении и в самом пуше</p>
            </div>
        )
    }

    render() {

        /* Fields */
        const { visible, onClose } = this.props
        const { saving, form } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={onClose}
                className="nt-form-modal"
                overlayClassName="nt-details-overlay"
                closeTimeoutMS={300}
            >

                <div className="nt-form">

                    <div className="nt-form-head">

                        <div className="nt-form-head-title">
                            <div className="nt-form-name">Новый шаблон</div>
                            <div className="nt-form-text">Заголовок, текст и фото — всё, что человек увидит в телефоне</div>
                        </div>

                        <div onClick={onClose} className="nt-details-close">
                            <Icon name="close" size={11} width={2.2} />
                        </div>

                    </div>

                    <div className="nt-form-body">

                        <div className="nt-form-column">

                            <div className="nt-field">
                                <div className="nt-label">Получатели</div>
                                <div className="nt-tabs">
                                    {TYPES.map(item =>
                                        <div
                                            key={item.key}
                                            onClick={() => this.setForm("type", item.key)}
                                            className={`nt-tab ${form.type === item.key ? "active" : ""}`}
                                        >
                                            {item.text}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="nt-field">
                                <div className="nt-label">Заголовок *</div>
                                <input ref={this._title} className="nt-input" type="text" placeholder="Например: Скидка 20% на маникюр" value={form.title} onChange={event => this.setForm("title", event.target.value)} />
                                {this._emoji("title")}
                            </div>

                            <div className="nt-field">
                                <div className="nt-label">Текст уведомления *</div>
                                <textarea ref={this._description} className="nt-textarea" placeholder="Введите текст..." value={form.description} onChange={event => this.setForm("description", event.target.value)} rows={4} />
                                {this._emoji("description")}
                            </div>

                            <div className="nt-field">
                                <div className="nt-label">Фото</div>
                                {this._image()}
                                <input ref={this._file} type="file" accept="image/*" style={{ display: "none" }} onChange={this.pickImage} />
                            </div>

                            <div className="nt-row">

                                <div className="nt-field">
                                    <div className="nt-label">Кнопка</div>
                                    <input className="nt-input" type="text" placeholder="Например: Перейти" value={form.button} onChange={event => this.setForm("button", event.target.value)} />
                                </div>

                                <div className="nt-field nt-field-color">
                                    <div className="nt-label">Цвет кнопки</div>
                                    <div className="nt-color">
                                        <input type="color" value={form.buttonColor} onChange={event => this.setForm("buttonColor", event.target.value)} />
                                        <span>{form.buttonColor}</span>
                                    </div>
                                </div>

                            </div>

                            <div className="nt-row">

                                <div className="nt-field">
                                    <div className="nt-label">Экран</div>
                                    <input className="nt-input" type="text" placeholder="Например: BookingInner" value={form.screen} onChange={event => this.setForm("screen", event.target.value)} />
                                </div>

                                <div className="nt-field">
                                    <div className="nt-label">Параметры</div>
                                    <input className="nt-input" type="text" placeholder='{"id": 1}' value={form.params} onChange={event => this.setForm("params", event.target.value)} />
                                </div>

                            </div>

                            <div className="nt-form-note">Кнопка появится, только если заполнены и её текст, и экран перехода</div>

                        </div>

                        <div className="nt-form-side">

                            <div className="nt-form-side-text">Предпросмотр</div>

                            <Preview
                                dark
                                title={form.title}
                                description={form.description}
                                button={form.button}
                                buttonColor={form.buttonColor}
                                image={form.image}
                            />

                            <div className="nt-form-hint">Шаблон сохраняется черновиком — получателей выберете при отправке</div>

                        </div>

                    </div>

                    <div className="nt-form-footer">
                        <div onClick={onClose} className="nt-button light">Отмена</div>
                        <div onClick={this.save} className={`nt-button ${saving ? "disabled" : ""}`}>{saving ? "Сохранение..." : "Сохранить шаблон"}</div>
                    </div>

                </div>

            </Modal>
        )
    }
}

export default CreateModal
